import { useRequest } from "ahooks";
import dayjs from "dayjs";
import { isObject } from "lodash";

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import { GridColDef } from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";

import { ApiHotelItem, getHotelCalendar } from "@/api/hotel";
import AlertModal from "@/components/AlertModal";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import { AlertTypeEnum } from "@/constants";
import { CabinsColors, FREE_MAX_HOTEL_VIEW } from "@/constants/config";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";
import { useQuery } from "@/hooks/useQuery";
import useThemeData from "@/hooks/useThemeData";

import HotelItem from "../Hotel/components/HotelItem/index";
import { RoutesItemSkeleton } from "../Hotel/components/HotelListSkeleton";
import Calendar from "./Calendar";
import styles from "./index.module.css";

function HotelCalendar() {
  const modalRef = useRef<typeof AlertModal>();
  const { id } = useQuery<{ id: string }>();
  const navigate = useNavigate();
  const theme = useThemeData();
  const { data, loading, error } = useRequest(() =>
    getHotelCalendar({ hotel_id: decodeURIComponent(id), date: dayjs().format("YYYY-MM-DD") })
  );
  const { isMobile } = usePlatform();
  const [mode, setMode] = useState<"calendar" | "list" | string>("list");
  const store = useGlobalStoreContext();

  const align = isMobile ? "left" : "center";

  const dataSource = useMemo(() => {
    if (data !== undefined && data?.data && data?.data[0]) {
      return Object.values(data?.data[0])
        .filter((v) => isObject(v))
        .flat()
        .sort((a, b) => {
          return dayjs(a.avail_date).isAfter(b.avail_date) ? 1 : -1;
        })
        .map((item) => {
          return {
            ...item,
            point_value: Number(item.point_value),
            cash_price: Number(item.cash_price),
          };
        });
    }

    return [];
  }, [data]);

  const showDays = useMemo(
    () =>
      [...new Array(store?.user_type === 0 ? 60 : 365)].map((_, i) =>
        dayjs().add(i, "day").format("YYYY-MM-DD")
      ),
    [store?.user_type]
  );

  const handleCreateAlert = useEvent((data: ApiHotelItem) => {
    modalRef.current?.show(AlertTypeEnum.Hotel, {
      alert_type: AlertTypeEnum.Hotel,
      fromLocation: [data.id],
      toLocation: [data.id],
      hotelName: [data.name],
    });
  });

  const handleModeChange = useEvent((event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.value);
  });

  const getCalendarItemData = useCallback(
    (date: string) => {
      const data = dataSource
        .filter((ele) => dayjs(ele.avail_date).isSame(date, "D"))
        .sort((a, b) => {
          return Number(a.points_rate) - Number(b.points_rate);
        });

      return {
        date,
        showDate: showDays.length ? showDays.includes(date) : true,
        points: [
          { color: CabinsColors.economy },
          { color: CabinsColors.premiumEconomy },
          { color: CabinsColors.business },
        ].map((item, index) => ({
          value:
            data[index] && data[index]?.points_rate
              ? `${data[index]?.points_rate / 1000}K pts${
                  isMobile ? "" : " | $" + data[index]?.cash_price
                } `
              : "",
          color: item.color,
          unit: "",
          type: "",
          date,
          url: data[index]?.res_link,
          sourceData: data[index],
        })),
      };
    },
    [dataSource, showDays]
  );

  const columns: GridColDef[] = [
    {
      field: "avail_date",
      headerName: "Date",
      flex: 1,
      align,
      minWidth: 120,
      cellClassName: (params) => {
        return [0, 6].includes(dayjs(params.row.avail_date).day())
          ? theme.isDarkMode
            ? styles.hight_dark
            : styles.hight
          : "";
      },
      renderCell: (params) => {
        return isMobile ? (
          <div>
            <div>{dayjs(params.row.avail_date).format("ddd, MMM DD")}</div>
            <div>{dayjs(params.row.avail_date).format(" YYYY")}</div>
          </div>
        ) : (
          <div>
            <div>{dayjs(params.row.avail_date).format("ddd, MMM DD YYYY")}</div>
          </div>
        );
      },
    },
    {
      field: "cash_price",
      headerName: "Cash",
      flex: 1,
      align,
      minWidth: 100,
      renderCell(params) {
        return (
          <span>
            {params.row.cash_price ? `$${Number(params.row.cash_price).toLocaleString()}` : "-"}
          </span>
        );
      },
    },
    {
      field: "points_rate",
      headerName: "Pts",
      flex: 1,
      align,
      minWidth: 100,
      renderCell(params) {
        return <span>{params.row.points_rate ? `${params.row.points_rate / 1000}K` : "-"}</span>;
      },
    },
    {
      field: "point_value",
      headerName: "Value",
      flex: 1,
      align,
      minWidth: 120,
      renderCell(params) {
        return <span>{params.row.point_value ? `${params.row.point_value}¢` : "-"}</span>;
      },
    },
    {
      field: "rate_plan",
      headerName: "Room",
      flex: 1,
      align,
      minWidth: 160,
      sortable: false,
    },
  ];

  if (error) {
    <div className="max-w-5xl mx-auto mt-[20px]">
      <Paper sx={{ minHeight: "100vh" }}>
        <Typography className="text-center pt-[100px]">{error.message}</Typography>
      </Paper>
    </div>;
  }
  if (data?.hide_data) {
    return (
      <div className="max-w-5xl mx-auto mt-[20px]">
        <Paper sx={{ minHeight: "100vh" }}>
          {data?.hotel_info ? (
            <HotelItem
              data={data?.hotel_info}
              usedDetails
              onLoad={() => {
                console.log("");
              }}
              onAlert={handleCreateAlert}
            />
          ) : null}
          <div className={isMobile ? "mt-[20px] text-center" : "mt-[80px] text-center"}>
            <Typography color="textSecondary">You've reached your today's limit.</Typography>
            <Typography color="textSecondary">
              Free users have access to {FREE_MAX_HOTEL_VIEW} hotel detail pages per day.
            </Typography>
            <Typography color="textSecondary">
              <Typography color="primary" fontWeight={"bold"} className="inline !mx-[4px]">
                PRO
              </Typography>
              users enjoy{" "}
              <Typography color="primary" fontWeight={"bold"} className="inline !mx-[4px]">
                unlimited
              </Typography>{" "}
              yearly availability views for hotels.
            </Typography>
            <Button
              onClick={() => {
                navigate("/pro");
              }}
              sx={{ mt: 2, fontWeight: "bold" }}
              variant="contained"
            >
              Go PRO
            </Button>
          </div>
        </Paper>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-[20px]">
      <TableContainer
        component={Paper}
        sx={{
          minHeight: "100vh",
          ".MuiDataGrid-root": {
            border: "none",
          },
        }}
      >
        {loading ? (
          <div className="px-[10px]">
            <RoutesItemSkeleton usedDetails />
            <Box>
              {new Array(10).fill("1").map((_, index) => (
                <Skeleton key={index} animation="wave" height={80} />
              ))}
            </Box>
          </div>
        ) : (
          <>
            {data?.hotel_info ? (
              <HotelItem
                data={data?.hotel_info}
                usedDetails
                onLoad={() => {
                  console.log("");
                }}
                onAlert={handleCreateAlert}
              />
            ) : null}

            <div className="px-[15px]">
              <FormControl>
                <RadioGroup
                  row
                  name="mode-radio-buttons-group"
                  value={mode}
                  onChange={handleModeChange}
                >
                  <FormControlLabel
                    value="list"
                    control={<Radio />}
                    label={<Typography color="textPrimary">List View</Typography>}
                  />
                  <FormControlLabel
                    value="calendar"
                    control={<Radio />}
                    label={<Typography color="textPrimary">Calendar View</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {mode === "list" ? (
              <DataGridPro
                getRowId={(row) => row.avail_date + row.rate_plan}
                rows={dataSource}
                columns={columns}
                onRowClick={(params) => {
                  window.open(params.row.res_link, "_blank");
                }}
                sx={
                  isMobile
                    ? {}
                    : {
                        ".MuiDataGrid-columnHeaderTitleContainer": {
                          justifyContent: "center",
                        },
                      }
                }
              />
            ) : (
              <Calendar
                getItemData={getCalendarItemData}
                onItemClick={(item) => window.open(item.url, "_blank")}
              />
            )}

            <ScrollTopBar />
          </>
        )}
      </TableContainer>

      <AlertModal
        ref={modalRef}
        onReset={() => {
          console.log("reset");
        }}
      />
    </div>
  );
}

export default HotelCalendar;
